import { useState, useEffect, useCallback } from 'react'
import { agentFilesService } from '../services/agentFilesService'
import type { FileNode, FileVersion } from '../types/agentFiles.types'
import { useUser } from '../contexts/UserContext'

export function useAgentFiles(agentId: string) {
    const { user } = useUser()
    const [files, setFiles] = useState<FileNode[]>([])
    const [currentFile, setCurrentFile] = useState<string | null>(null)
    const [content, setContent] = useState<string>('')
    const [isLoading, setIsLoading] = useState(false)
    const [isSaving, setIsSaving] = useState(false)
    const [isDirty, setIsDirty] = useState(false)
    const [error, setError] = useState<string | null>(null)

    // Carregar lista de arquivos
    const loadFiles = useCallback(async () => {
        if (!agentId) return

        try {
            setIsLoading(true)
            setError(null)
            const fileList = await agentFilesService.listFiles(agentId)
            setFiles(fileList)
        } catch (err) {
            console.error('Error loading files:', err)
            setError('Erro ao carregar arquivos')
        } finally {
            setIsLoading(false)
        }
    }, [agentId])

    // Abrir arquivo
    const openFile = useCallback(async (path: string) => {
        if (!agentId) return

        try {
            setIsLoading(true)
            setError(null)
            const fileContent = await agentFilesService.readFile(agentId, path)
            setContent(fileContent)
            setCurrentFile(path)
            setIsDirty(false)
        } catch (err) {
            console.error('Error opening file:', err)
            setError(`Erro ao abrir arquivo: ${path}`)
        } finally {
            setIsLoading(false)
        }
    }, [agentId])

    // Salvar arquivo
    const saveFile = useCallback(async (message?: string) => {
        if (!agentId || !currentFile) return

        try {
            setIsSaving(true)
            setError(null)
            await agentFilesService.writeFile(
                agentId,
                currentFile,
                content,
                message || 'Manual save',
                { type: 'user', id: user?.id || '' }
            )
            setIsDirty(false)
            // Recarregar lista de arquivos para atualizar versões
            await loadFiles()
        } catch (err) {
            console.error('Error saving file:', err)
            setError('Erro ao salvar arquivo')
            throw err
        } finally {
            setIsSaving(false)
        }
    }, [agentId, currentFile, content, loadFiles, user])

    // Criar novo arquivo
    const createFile = useCallback(async (path: string, initialContent: string = '') => {
        if (!agentId) return

        try {
            setIsLoading(true)
            setError(null)
            await agentFilesService.writeFile(
                agentId,
                path,
                initialContent,
                'Create new file',
                { type: 'user', id: user?.id || '' }
            )
            await loadFiles()
            await openFile(path)
        } catch (err) {
            console.error('Error creating file:', err)
            setError('Erro ao criar arquivo')
            throw err
        } finally {
            setIsLoading(false)
        }
    }, [agentId, loadFiles, openFile, user])

    // Deletar arquivo
    const deleteFile = useCallback(async (path: string) => {
        if (!agentId) return

        try {
            setIsLoading(true)
            setError(null)
            await agentFilesService.deleteFile(agentId, path)
            if (currentFile === path) {
                setCurrentFile(null)
                setContent('')
            }
            await loadFiles()
        } catch (err) {
            console.error('Error deleting file:', err)
            setError('Erro ao deletar arquivo')
            throw err
        } finally {
            setIsLoading(false)
        }
    }, [agentId, currentFile, loadFiles])

    // Obter histórico de versões
    const getHistory = useCallback(async (path: string): Promise<FileVersion[]> => {
        if (!agentId) return []

        try {
            return await agentFilesService.getVersionHistory(agentId, path)
        } catch (err) {
            console.error('Error getting history:', err)
            setError('Erro ao carregar histórico')
            return []
        }
    }, [agentId])

    // Restaurar versão
    const restoreVersion = useCallback(async (path: string, version: number) => {
        if (!agentId) return

        try {
            setIsLoading(true)
            setError(null)
            await agentFilesService.restoreVersion(agentId, path, version)
            if (currentFile === path) {
                await openFile(path)
            }
            await loadFiles()
        } catch (err) {
            console.error('Error restoring version:', err)
            setError('Erro ao restaurar versão')
            throw err
        } finally {
            setIsLoading(false)
        }
    }, [agentId, currentFile, loadFiles, openFile])

    // Carregar arquivos ao montar
    useEffect(() => {
        loadFiles()
    }, [loadFiles])

    // Atualizar conteúdo e marcar como modificado
    const updateContent = useCallback((newContent: string) => {
        setContent(newContent)
        setIsDirty(true)
    }, [])

    return {
        files,
        currentFile,
        content,
        isLoading,
        isSaving,
        isDirty,
        error,
        loadFiles,
        openFile,
        saveFile,
        createFile,
        deleteFile,
        getHistory,
        restoreVersion,
        updateContent
    }
}
